import React, { useCallback, useEffect, useMemo, useState } from "react";

import styled from "styled-components/macro";

import PaymentPage from "Payments/PaymentPage";
import { AnimatePresence, motion } from "framer-motion";

import API from "./API";
import Confetti from "./Confetti";
import PaymentDrawerError from "./PaymentDrawerError";

export default function PaymentsDrawer({
  show,
  onClose,
  url,
  reFetchSignupListInfo,
  postData,
  fundID,
  successfulPayment,
  setSuccessfulPayment,
  isSuggestedDonation,
  hideConfetti,
}) {
  const [shouldFetchPaymentPageData, setShouldFetchPaymentPageData] =
    useState(true);
  const [paymentPageDataError, setPaymentPageDataError] = useState(false);
  const [paymentIsProcessing, syncPaymentIsProcessing] = useState(false);

  const handleClose = useCallback(() => {
    setShouldFetchPaymentPageData(true);
    onClose();
  }, [onClose]);

  const paymentParent = useMemo(
    () => ({
      parent: "signups",
      hideCardStyles: true,
      hideDefaultBackground: true,
      successCallback: () => {
        reFetchSignupListInfo();
        setSuccessfulPayment(true);
      },
      successScreen: {
        title: "You're all done signing up!",
        subtitle: `Thank you for your ${
          isSuggestedDonation ? "donation" : "payment"
        }.`,
        receiptButton: {
          text: "Review your signup",
          description: "You're all done. Thanks.",
          action: handleClose,
        },
      },
      hidePaymentsConfetti: true,
      themeColor: "#015f98",
      syncPaymentIsProcessing,
    }),
    [
      handleClose,
      isSuggestedDonation,
      reFetchSignupListInfo,
      setSuccessfulPayment,
    ]
  );

  useEffect(
    function getAndSetPaymentPageData() {
      if (!shouldFetchPaymentPageData || !fundID || postData?.amount === null)
        return;

      setShouldFetchPaymentPageData(false);
      API.getPaymentPageData(fundID, postData)
        .post()
        .then((res) => {
          window.data.paymentPageData = res.data;
        })
        .catch((err) => {
          console.log("err", err);
          setPaymentPageDataError(true);
        });
    },
    [url, postData, fundID, shouldFetchPaymentPageData]
  );

  return (
    <AnimatePresence>
      {show && (
        <OverlayContainer
          key="overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <Drawer
            key="drawer"
            initial={{ right: -450 }}
            animate={{ right: 0 }}
            exit={{ right: successfulPayment ? "-100%" : -450 }}
            $successfulPayment={successfulPayment}
          >
            {!successfulPayment && !paymentIsProcessing ? (
              <CancelButton onClick={handleClose}>
                <i className="far fa-chevron-left"></i>
                <span style={{ marginLeft: "8px" }}>Cancel</span>
              </CancelButton>
            ) : null}
            {paymentPageDataError ? (
              <PaymentDrawerError />
            ) : (
              <>
                <Confetti hide={!successfulPayment || hideConfetti} />
                <PaymentPage paymentParent={paymentParent} />
              </>
            )}
          </Drawer>
        </OverlayContainer>
      )}
    </AnimatePresence>
  );
}

const OverlayContainer = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  z-index: 40;
  overflow-y: auto;
`;

const Drawer = styled(motion.div)`
  position: absolute;
  min-height: 100%;
  width: 100%;
  top: 0;
  background: ${(props) =>
    props.$successfulPayment
      ? "linear-gradient(165deg, hsl(0 0% 94%), hsl(0 0% 90%))"
      : "linear-gradient(165deg, hsl(0 0% 98%), hsl(0 0% 95%))"};
  z-index: 40;
  transition: width ease 0.3s;

  @media (min-width: 450px) {
    width: ${(props) => (props.$successfulPayment ? "100%" : "420px")};
  }
`;

const CancelButton = styled.button`
  position: absolute;
  top: 14px;
  left: 10px;
  line-height: 1;
  border: none;
  background: none;
  font-weight: 400;
  font-size: 20px;
  color: #4d4d4d;
  opacity: 0.75;
  transition: all ease 0.2s;

  &:hover {
    cursor: pointer;
    color: #015f98;
    opacity: 1;
    scale: 1.1;
  }
`;
